import './HomeComposer.css'
import { useId, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { syncTextareaAutosizeHeight } from '@/lib/utils/sync-textarea-autosize-height'
import { ArrowRight, Loader2 } from '@digital-twins/design-system/icons'
import { MessageLengthHint, messageCharacterCount } from '@/components/composer/MessageLengthHint'
import { cn } from '@/lib/utils'
import { useAutosizeTextarea } from '@/lib/hooks/use-autosize-textarea'
import {
  CommandAutocomplete,
  CommandOption,
  filterCommandOptions,
} from '@/components/chat/CommandAutocomplete'
import {
  findKnownSlashToken,
  findTrailingSlashToken,
  replaceSlashToken,
  stripSlashToken,
} from '@/lib/composer/slashCommandToken'

const HOME_COMPOSER_TEXTAREA_MAX_PX = 200
/** Home Lab hero composer — taller ceiling for the larger type scale. */
const HOME_COMPOSER_TEXTAREA_MAX_PX_LG = 280

/**
 * Size variants. `default` is the live homepage and MUST stay byte-identical: every
 * class string below is the untouched original. `lg` is the Home Lab hero composer.
 */
type HomeComposerSize = 'default' | 'lg'

const SIZE_CLASSES: Record<
  HomeComposerSize,
  { shell: string; textarea: string; maxPx: number }
> = {
  default: {
    // Surface classes live HERE, per size, not on the shared wrapper below: a
    // wrapper-level `bg-*` and the `lg` variant's own `bg-*` collide in twMerge
    // and the later one silently wins, which is how the Home Lab composer lost
    // its token and started painting the homepage card instead.
    shell: [
      'min-h-[137px] w-full rounded-2xl px-4 py-4 md:px-6',
      'bg-[var(--home-composer-bg)] shadow-[var(--home-composer-shadow)]',
    ].join(' '),
    textarea: 'max-h-[200px] text-[18px]',
    maxPx: HOME_COMPOSER_TEXTAREA_MAX_PX,
  },
  lg: {
    shell: [
      // Home stays slim on desktop; mobile keeps a separate control row.
      // Textarea growth and armed chips remain in the existing flow.
      'flex min-h-[64px] w-full flex-col rounded-3xl px-4 py-2 md:flex-row md:items-center md:gap-3 md:px-5',
      // Home elevation is separate from the shared chat composer's surface.
      'border border-[var(--home-lab-composer-border)] bg-[var(--home-lab-composer-bg)] [box-shadow:var(--home-lab-composer-shadow)]',
    ].join(' '),
    textarea: [
      'max-h-[280px] text-[15px] md:text-[16px]',
      'placeholder:text-[var(--home-lab-composer-placeholder-fg)]',
    ].join(' '),
    maxPx: HOME_COMPOSER_TEXTAREA_MAX_PX_LG,
  },
}

interface HomeComposerProps {
  value: string
  messageLimit?: number
  onValueChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  submitLabel: string
  disabled?: boolean
  rows?: number
  className?: string
  /**
   * Slot AFTER the placeholder, on the same row as the textarea: the armed
   * workflow, the active study type, and later attachments. Kept separate from
   * `footer` because the footer holds the DOORS (pickers you open) while this
   * holds the STATE (what those pickers turned on). Inline and line-height
   * capped so arming a workflow does not grow the first line. Omitted on the
   * live homepage, which renders exactly as before.
   */
  header?: ReactNode
  /** Slot BEFORE the textarea on the input row: the attach `+`, as in the thread composer. */
  leading?: ReactNode
  footer?: ReactNode
  /** Reserve bottom row height (e.g. study mode) so toggling chat/study does not jump layout. */
  reserveFooterSlot?: boolean
  /** When true, allow submitting even with empty text (e.g. image-only sends). */
  hasAttachments?: boolean
  /**
   * Blocks submit WITHOUT dimming the composer. `disabled` is for "this whole
   * surface is unavailable"; this is for "you still owe me a required choice",
   * where the user must stay able to type and to use the footer controls.
   */
  submitBlocked?: boolean
  /**
   * Fired when the user clicks send (or Enter) while `submitBlocked` is true.
   * The button stays clickable so the page can explain what is still owed
   * instead of silently doing nothing.
   */
  onBlockedSubmit?: () => void
  /**
   * A submit that is already running. Blocks re-submission and shows a pending
   * treatment on the button, WITHOUT dimming the surface.
   *
   * Third leg of the split above: `disabled` says the whole surface is
   * unavailable, `submitBlocked` says a required choice is still owed (so the
   * user must stay able to type and use the footer controls), and `isBusy` says
   * the thing the user asked for is already happening. It keeps the same
   * "stay usable" rule as `submitBlocked`, plus the button itself has to show
   * that the click landed.
   */
  isBusy?: boolean
  /** Visual scale. Omit (or 'default') for the live homepage; 'lg' is the Home Lab hero. */
  size?: HomeComposerSize
  /**
   * Slash commands. A leading `/` opens the autocomplete; a `/`-leading submit
   * goes to `onCommand` instead of `onSubmit`. Omit and `/` is ordinary prose.
   */
  commands?: Array<CommandOption>
  onCommand?: (command: string, rest: string) => boolean
  /**
   * Bump to focus the textarea. Used by the sidebar Ask CTA after it lands a
   * fresh Home Lab conversation on a page that was already mounted.
   */
  focusSignal?: number
}

export function HomeComposer({
  value,
  messageLimit,
  onValueChange,
  onSubmit,
  placeholder,
  submitLabel,
  disabled = false,
  rows = 1,
  className,
  header,
  leading,
  footer,
  reserveFooterSlot = false,
  hasAttachments = false,
  submitBlocked = false,
  onBlockedSubmit,
  isBusy = false,
  size = 'default',
  commands,
  onCommand,
  focusSignal = 0,
}: HomeComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const controlsRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [availableWidth, setAvailableWidth] = useState(0)
  const reduceMotion = useReducedMotion()
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (focusSignal) {
      textareaRef.current?.focus()
    }
  }, [focusSignal])
  const [isCommandDismissed, setIsCommandDismissed] = useState(false)
  const lengthHintId = useId()
  const messageTooLong = messageLimit !== undefined && messageCharacterCount(value) > messageLimit
  const hasContent = value.trim().length > 0 || hasAttachments
  const showSubmit = size !== 'lg' || hasContent || isBusy
  const canSubmit = hasContent && !disabled && !submitBlocked && !isBusy && !messageTooLong
  const canNudge = hasContent && !disabled && submitBlocked && !isBusy && !messageTooLong && Boolean(onBlockedSubmit)
  const showFooterRow = Boolean(footer) || reserveFooterSlot
  const sizeClasses = SIZE_CLASSES[size]
  /* The token being typed, wherever it sits: a `/` opens the list at the start
     of the text or after any whitespace (Slack's convention), and the list
     closes the moment whitespace follows the token, because from then on the
     reader is writing the rest of the message. */
  const trailingToken =
    commands && commands.length > 0 && onCommand && isFocused && !isCommandDismissed
      ? findTrailingSlashToken(value)
      : null
  const commandQuery = trailingToken ? trailingToken.name.slice(1) : null
  const commandRows =
    commandQuery === null ? [] : filterCommandOptions(commands ?? [], commandQuery)
  const hasCommandRows = commandRows.length > 0
  const isExactCommand =
    trailingToken !== null &&
    commandRows.length === 1 &&
    commandRows[0].command.toLowerCase() === trailingToken.name.toLowerCase()

  useAutosizeTextarea(textareaRef, value, {
    maxHeightPx: sizeClasses.maxPx,
    disabled,
  })

  // Measure against the compact row even while expanded, so wrapping does not
  // oscillate when giving the text its full row makes it fit on one line again.
  useLayoutEffect(() => {
    if (size !== 'lg' || !shellRef.current || !controlsRef.current) {
      return
    }
    const shell = shellRef.current
    const controls = controlsRef.current
    const measure = () => {
      const style = getComputedStyle(shell)
      const inlineControls = window.innerWidth >= 768
      setAvailableWidth(shell.clientWidth - parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight) - (inlineControls ? controls.offsetWidth + 12 : 0))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(shell)
    observer.observe(controls)
    return () => observer.disconnect()
  }, [size])

  useLayoutEffect(() => {
    const input = textareaRef.current
    const shell = shellRef.current
    if (size !== 'lg' || !input || !shell || availableWidth <= 0) {
      return
    }
    const probe = input.cloneNode() as HTMLTextAreaElement
    probe.removeAttribute('data-testid')
    probe.removeAttribute('placeholder')
    probe.setAttribute('aria-hidden', 'true')
    probe.tabIndex = -1
    probe.value = value
    const headerWidth = input.nextElementSibling?.getBoundingClientRect().width ?? 0
    const leadingWidth = input.previousElementSibling?.getBoundingClientRect().width ?? 0
    Object.assign(probe.style, {
      position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
      height: '0px', minHeight: '0px', maxHeight: 'none',
      width: `${Math.max(1, availableWidth - headerWidth - (headerWidth ? 8 : 0) - leadingWidth - (leadingWidth ? 8 : 0))}px`,
    })
    shell.appendChild(probe)
    setIsExpanded(probe.scrollHeight > parseFloat(getComputedStyle(input).lineHeight) + 1)
    probe.remove()
  }, [size, value, availableWidth, header, leading])

  useLayoutEffect(() => {
    if (size === 'lg' && textareaRef.current) {
      syncTextareaAutosizeHeight(textareaRef.current, sizeClasses.maxPx)
    }
  }, [size, isExpanded, availableWidth, sizeClasses.maxPx])

  function requestSubmit() {
    if (messageTooLong) {
      return
    }
    const text = value.trim()
    /* A known `/command` anywhere on the line names the command and the rest
       of the line is the message; a leading unknown one is still refused by
       the handler rather than sent as prose. */
    const token = onCommand
      ? findKnownSlashToken(
          text,
          (commands ?? []).map((option) => option.command),
        )
      : null
    if (onCommand && token) {
      const rest = stripSlashToken(text, token)
      if (!onCommand(token.name, rest)) {
        return
      }
      /* Only the token leaves the box. The words around it stay on screen
         while the command's confirm card is open, so the reader still sees
         what they wrote, and cancelling the card loses nothing. */
      onValueChange(rest)
      return
    }
    if (canSubmit) {
      onSubmit()
      return
    }
    if (canNudge) {
      onBlockedSubmit?.()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      hasCommandRows &&
      !event.shiftKey &&
      (event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        ((event.key === 'Enter' || event.key === 'Tab') && !isExactCommand))
    ) {
      return
    }

    /* Taking Enter/Tab back off a dropdown that is still on screen (the
       exact-match case). The dropdown listens on `document`, above React's
       root, so without stopping propagation it would ALSO see this key, pick
       the row, and write the command back into the box just cleared. Same
       guard as `ChatComposer`. */
    if (
      (event.key === 'Enter' || event.key === 'Tab') &&
      !event.shiftKey &&
      hasCommandRows &&
      isExactCommand
    ) {
      event.preventDefault()
      event.stopPropagation()
      requestSubmit()
      return
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      requestSubmit()
    }
  }

  return (
    <>
    <motion.div
      ref={shellRef}
      layout={size === 'lg' ? 'size' : undefined}
      transition={{ layout: { duration: reduceMotion ? 0 : 0.18, ease: 'easeOut' } }}
      className={cn(
        sizeClasses.shell,
        size === 'lg' && 'home-composer-lg',
        size === 'lg' && isExpanded && 'md:flex-col md:items-stretch md:gap-2 py-3',
        'text-[var(--home-composer-fg)]',
        commandQuery !== null && 'relative',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
    >
      {commandQuery !== null ? (
        <CommandAutocomplete
          options={commands ?? []}
          query={commandQuery}
          onSelect={(option) => {
            /* Replace the typed token in place (trailing space closes the
               list) so the words before it survive as the message. */
            onValueChange(
              trailingToken
                ? replaceSlashToken(value, trailingToken, `${option.command} `)
                : `${option.command} `,
            )
            setIsCommandDismissed(false)
          }}
          onDismiss={() => setIsCommandDismissed(true)}
          appearance={size === 'lg' ? 'internal' : 'external'}
        />
      ) : null}

      <motion.div
        layout={size === 'lg' ? 'position' : undefined}
        transition={{ layout: { duration: reduceMotion ? 0 : 0.18, ease: 'easeOut' } }}
        className={cn(
          'home-composer-input-row relative flex min-w-0 items-center gap-2',
          size === 'lg' && isExpanded && 'md:w-full',
          size === 'lg' ? 'mb-2 md:mb-0 md:flex-1 md:self-center' : showFooterRow ? 'mb-4' : 'mb-6',
        )}
        data-testid="home-composer-input-row"
      >
        {leading ? (
          <div
            className="home-composer-leading flex shrink-0 items-center gap-1.5 self-start -ml-1"
            data-testid="home-composer-leading"
          >
            {leading}
          </div>
        ) : null}

        <textarea
          ref={textareaRef}
          data-testid="home-composer-input"
          value={value}
          aria-invalid={messageTooLong || undefined}
          aria-describedby={messageLimit === undefined ? undefined : lengthHintId}
          onChange={(event) => {
            onValueChange(event.target.value)
            setIsCommandDismissed(false)
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          rows={rows}
          disabled={disabled}
          className={cn(
            'min-h-0 min-w-0 flex-1 resize-none border-0 bg-transparent p-0 leading-[1.5] outline-none focus:outline-none focus:ring-0',
            sizeClasses.textarea,
            'placeholder:text-[var(--home-composer-placeholder-fg)]',
          )}
          placeholder={placeholder}
        />

        {header ? (
          <div
            className="home-composer-header flex h-[1.5em] shrink-0 items-center gap-1.5 self-start"
            data-testid="home-composer-header"
          >
            {header}
          </div>
        ) : null}
      </motion.div>

      <motion.div
        ref={controlsRef}
        layout={size === 'lg' ? 'position' : undefined}
        transition={{ layout: { duration: reduceMotion ? 0 : 0.18, ease: 'easeOut' } }}
        className={cn(
          'home-composer-controls',
          size === 'lg' ? 'flex items-center' : 'flex items-end gap-2 md:gap-3',
          showFooterRow ? 'justify-between' : 'justify-end',
          size === 'lg' && 'md:ml-auto md:max-w-[60%] md:shrink-0',
          size === 'lg' && isExpanded && 'md:max-w-full',
        )}
      >
        {footer ? (
          <div
            className={cn(
              'min-w-0 flex-1',
              /* Internal pills pad themselves (`px-3`). Pull the row back so the
                 first icon lines up with the placeholder, not with the pill edge. */
              size === 'lg' && '-ml-1 md:-ml-3',
            )}
          >
            {footer}
          </div>
        ) : reserveFooterSlot ? (
          <div className="min-h-11 min-w-0 flex-1 shrink-0" aria-hidden />
        ) : null}

        <button
          type="button"
          onClick={requestSubmit}
          disabled={!canSubmit && !canNudge}
          aria-label={submitLabel}
          aria-hidden={showSubmit ? undefined : true}
          tabIndex={showSubmit ? undefined : -1}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-md bg-cta text-cta-foreground duration-200 enabled:hover:-translate-y-0.5 enabled:hover:opacity-90 disabled:pointer-events-none',
            size === 'lg'
              ? 'h-10 overflow-hidden rounded-lg transition-[width,margin,transform,opacity] ease-out motion-reduce:transition-none'
              : 'h-9 w-9 transition-[transform,opacity] md:h-12 md:w-12 md:rounded-lg',
            size === 'lg' &&
              (showSubmit
                ? 'ml-2 w-10 scale-100 opacity-100 md:ml-3'
                : 'w-0 scale-90 opacity-0 pointer-events-none'),
            size === 'lg'
              ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
              : 'disabled:opacity-40',
          )}
          data-testid="home-composer-submit"
        >
          {isBusy ? (
            <Loader2
              className="h-4 w-4 animate-spin md:h-5 md:w-5"
              strokeWidth={2.2}
              aria-hidden
              data-testid="home-composer-submit-pending"
            />
          ) : (
            <ArrowRight className="h-4 w-4 shrink-0 md:h-5 md:w-5" strokeWidth={2.2} />
          )}
        </button>
      </motion.div>
    </motion.div>
    <MessageLengthHint value={value} limit={messageLimit} id={lengthHintId} />
    </>
  )
}
